<template>
  <div class="tpr-page bg-light-page">

    <div v-if="isLoading" class="w-100 py-5 text-center">
      <loader />
    </div>

    <template v-else-if="error">
      <div class="p-4">
        <ErrorMessage :message="error" />
      </div>
      <div class="px-4">
        <button class="tp-btn-primary w-100 font-semibold" type="button" @click="$router.push({ name: 'ConciergeMainPage' })">
          Back to Home
        </button>
      </div>
    </template>

    <template v-else>
      <!-- Hero -->
      <div :class="['tpr-hero text-center', { 'tpr-hero--complete': isComplete, 'tpr-hero--cancelled': isCancelled }]">
        <div :class="['tpr-icon', { 'tpr-icon--show': iconVisible, 'tpr-icon--complete': isComplete }]">
          <XCircle v-if="isCancelled" :size="40" :stroke-width="1.5" />
          <CheckCircle v-else :size="40" :stroke-width="1.5" />
        </div>
        <p class="body-3 mb-1" style="color:rgba(255,255,255,0.8);text-transform:uppercase;letter-spacing:0.8px">
          {{ statusLabel }}
        </p>
        <h2 class="font-bold mb-2" style="color:#fff;font-size:22px;letter-spacing:-0.2px">{{ statusTitle }}</h2>
        <p class="body-3 mb-0" style="color:rgba(255,255,255,0.85);max-width:260px;margin:0 auto;line-height:1.55">
          {{ statusDesc }}
        </p>
      </div>

      <!-- Receipt -->
      <div class="tpr-body">
        <div :class="['tp-receipt-card mt-4', { 'tp-receipt-card--complete': isComplete }]">
          <div class="text-center pb-3" style="border-bottom:1px solid #EEF1F5;margin-bottom:4px">
            <p class="body-3 mb-1 tp-label">{{ isComplete ? 'Tip paid' : 'Amount tipped' }}</p>
            <p class="font-bold mb-0" :style="{ fontSize: '26px', letterSpacing: '-0.3px', color: isComplete ? '#1A8A4A' : 'var(--merchant-primary-color, #2B5F7A)' }">
              {{ $formatCurrency(tipAmount) }}
            </p>
            <p v-if="isComplete" class="tpr-paid-badge mt-2 mb-0">Payment confirmed</p>
          </div>
          <div v-for="row in receiptRows" :key="row.label" class="tp-receipt-row">
            <span class="body-3">{{ row.label }}</span>
            <span class="body-3-3-2 text-right" style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ row.value }}</span>
          </div>

          <!-- VA / Payment instructions — hidden when complete -->
          <template v-if="!isComplete && !isPivotPending && (vaNumber || paymentInfo)">
            <div class="tpr-divider my-3"></div>
            <p class="body-3 mb-2 tp-label">Payment Instructions</p>

            <!-- VA Number highlighted block -->
            <div v-if="vaNumber" class="tpr-va-block mb-2">
              <p class="body-3 mb-1" style="color:var(--merchant-primary-color,#2B5F7A);opacity:0.7">Virtual Account Number</p>
              <div class="tpr-va-number-row">
                <span class="tpr-va-number">{{ vaNumber }}</span>
                <button class="tpr-copy-btn" type="button" @click="copyVaNumber">
                  <span v-if="vaCopied" class="tpr-copy-feedback">Copied!</span>
                  <template v-else>
                    <Copy :size="15" :stroke-width="2" />
                    <span style="font-size:12px">Copy</span>
                  </template>
                </button>
              </div>
            </div>

            <div v-for="info in paymentInfo" :key="info.label" class="tp-receipt-row">
              <span class="body-3">{{ info.label }}</span>
              <span class="body-2-1 text-right" style="max-width:200px;word-break:break-all">{{ info.value }}</span>
            </div>
          </template>
        </div>
      </div>

      <div class="tpr-footer">
        <transition name="tpr-flash">
          <div v-if="statusMessage" class="tpr-flash-msg mb-2">
            {{ statusMessage }}
          </div>
        </transition>
        <button
          v-if="isPivotPending && pivotPaymentUrl"
          class="tp-btn-primary w-100 font-semibold mb-2"
          type="button"
          @click="$openLink(pivotPaymentUrl)"
        >
          Complete Payment
        </button>
        <button
          v-if="txStatus === 'WAITING_PAYMENT'"
          class="tp-btn-outline w-100 font-semibold mb-2"
          type="button"
          :disabled="isRefreshing"
          @click="checkStatus"
        >
          <RefreshCw :size="15" :stroke-width="2" :class="{ 'tpr-spin': isRefreshing }" style="margin-right:6px" />
          {{ isRefreshing ? 'Checking...' : 'Check Payment Status' }}
        </button>
        <button class="tp-btn-primary w-100 font-semibold" type="button" @click="$router.push({ name: 'ConciergeMainPage' })">
          Back to Home
        </button>
      </div>
    </template>

  </div>
</template>

<script>
import { CheckCircle, XCircle, Copy, RefreshCw } from 'lucide-vue'
import { get } from '@/services/index.js'
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'ConciergeTipResult',

  components: { CheckCircle, XCircle, Copy, RefreshCw },

  data() {
    return {
      isLoading: true,
      error: '',
      txData: null,
      iconVisible: false,
      vaCopied: false,
      isRefreshing: false,
      statusMessage: '',
    }
  },

  computed: {
    ...mapGetters(['getMerchant', 'getConciergeHomeAuth']),

    txServiceId() {
      return this.$route.params.tx_service_id
    },

    txStatus() {
      return this.txData && this.txData.status
    },

    isComplete() {
      return this.txStatus === 'COMPLETE' || this.txStatus === 'PAID'
    },

    isCancelled() {
      return this.txStatus === 'CANCELLED' || this.txStatus === 'CANCEL'
    },

    statusLabel() {
      if (this.isCancelled) return 'Payment cancelled'
      return this.isComplete ? 'Tip sent!' : 'Payment submitted'
    },

    statusTitle() {
      if (this.isCancelled) return 'Transaction Cancelled'
      return this.isComplete ? 'Thank you!' : 'Awaiting Payment'
    },

    statusDesc() {
      if (this.isCancelled) {
        return 'Your payment was cancelled. No charge has been made.'
      }
      if (this.isComplete) {
        return 'Our team will be notified shortly. Your generosity means a lot to them.'
      }
      return 'Please complete the payment using the method you selected.'
    },

    tipAmount() {
      var detail = this.txData && this.txData.detail
      return (detail && detail.amount) || 0
    },

    receiptRows() {
      if (!this.txData) return []
      var paymentDetails = this.txData.payment_details || {}
      var rows = [
        { label: 'Invoice', value: this.txData.invoice || '-' },
        { label: 'Payment method', value: paymentDetails.payment_method_name || '-' },
        { label: 'Payment status', value: paymentDetails.status || '-' },
        { label: 'Date & time', value: moment(this.txData.created_at).format('DD MMM YYYY, HH:mm') },
      ]
      return rows
    },

    isVirtualAccount() {
      var details = this.txData && this.txData.payment_details
      return details && details.payment_method === 'VIRTUAL_ACCOUNT'
    },

    isPivotPending() {
      var details = this.txData && this.txData.payment_details
      if (!details || this.isComplete || this.isCancelled) return false
      return this.$isPivotPayment(details.bank_name)
    },

    pivotPaymentUrl() {
      return this.$getPivotPaymentUrl(this.txData && this.txData.payment_details)
    },

    vaNumber() {
      var details = this.txData && this.txData.payment_details
      return (details && details.virtual_account_number) || null
    },

    paymentInfo() {
      var details = this.txData && this.txData.payment_details
      if (!details) return null
      var rows = []
      if (this.isVirtualAccount && details.total_payment && details.total_payment !== this.tipAmount) {
        rows.push({ label: 'Total to pay', value: this.$formatCurrency(details.total_payment) })
      }
      var bankDisplay = this.isVirtualAccount
        ? details.payment_bank_code
        : (details.bank_code || details.bank_name)
      if (bankDisplay) {
        rows.push({ label: 'Bank', value: bankDisplay })
      }
      var expiredAt = details.expired_date_utc || details.expired_date
      if (expiredAt) {
        rows.push({ label: 'Valid until', value: moment(expiredAt).format('DD MMM YYYY, HH:mm') })
      }
      return rows.length > 0 ? rows : null
    },
  },

  mounted() {
    this.fetchDetail()
  },

  methods: {
    checkStatus() {
      var self = this
      self.isRefreshing = true
      var auth = this.getConciergeHomeAuth
      get('CONCIERGE', 'service/tx-detail/' + this.txServiceId, {
        merchant_id: this.getMerchant.id,
        reservation_id: auth.reservation_id,
      }, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            var prevStatus = self.txData && self.txData.status
            self.txData = res.data
            if (res.data.status === prevStatus) {
              self.showStatusMessage('Payment is still pending. Please complete the transfer.')
            }
          }
          self.isRefreshing = false
        })
        .catch(function() {
          self.isRefreshing = false
        })
    },

    showStatusMessage(msg) {
      var self = this
      self.statusMessage = msg
      setTimeout(function() { self.statusMessage = '' }, 3000)
    },

    copyVaNumber() {
      var self = this
      navigator.clipboard.writeText(this.vaNumber).then(function() {
        self.vaCopied = true
        setTimeout(function() { self.vaCopied = false }, 2000)
      })
    },

    fetchDetail() {
      this.isLoading = true
      this.error = ''
      var auth = this.getConciergeHomeAuth
      get('CONCIERGE', 'service/tx-detail/' + this.txServiceId, {
        merchant_id: this.getMerchant.id,
        reservation_id: auth.reservation_id,
      }, { type: 'concierge_token_direct', token: auth.token })
        .then(function(response) {
          var res = response.data
          if (res.success) {
            this.txData = res.data
            this.$nextTick(function() {
              setTimeout(function() { this.iconVisible = true }.bind(this), 80)
            }.bind(this))
          } else {
            this.error = res.message || 'Could not load payment details.'
          }
          this.isLoading = false
        }.bind(this))
        .catch(function() {
          this.error = 'Could not load payment details.'
          this.isLoading = false
        }.bind(this))
    },
  },
}
</script>

<style scoped>
.tpr-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.tpr-hero {
  background: linear-gradient(155deg, var(--merchant-primary-color, #2B5F7A) 0%, #1a3d50 100%);
  padding: 64px 24px 40px;
  flex-shrink: 0;
}

.tpr-hero--complete {
  background: linear-gradient(155deg, #1e7d46 0%, #145c31 100%);
}

.tpr-hero--cancelled {
  background: linear-gradient(155deg, #8a3a3a 0%, #5c2323 100%);
}

.tpr-icon {
  width: 76px;
  height: 76px;
  border-radius: 999px;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin: 0 auto 14px;
  transform: scale(0.3);
  opacity: 0;
  transition: transform 0.5s cubic-bezier(0.2, 1.3, 0.4, 1), opacity 0.4s ease;
}

.tpr-icon--show { transform: scale(1); opacity: 1; }

.tpr-icon--complete.tpr-icon--show {
  animation: tpr-icon-pulse 1.8s ease-out 0.5s 2;
}

@keyframes tpr-icon-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.5); }
  70%  { box-shadow: 0 0 0 18px rgba(255,255,255,0); }
  100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
}

.tpr-body {
  flex: 1;
  overflow-y: auto;
  padding: 0 16px 16px;
}

.tpr-footer {
  flex-shrink: 0;
  background: #fff;
  border-top: 1px solid #EEF1F5;
  padding: 12px 16px 20px;
}

.tpr-divider { height: 1px; background: #E4E8EF; }

.tpr-va-block {
  background: #F0F5FA;
  border: 1.5px dashed var(--merchant-primary-color, #2B5F7A);
  border-radius: 10px;
  padding: 10px 14px;
}

.tpr-va-number-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.tpr-va-number {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  color: var(--merchant-primary-color, #2B5F7A);
  word-break: break-all;
}

.tpr-copy-btn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 600;
  transition: opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.tpr-copy-feedback {
  font-size: 12px;
  font-weight: 600;
}

.tp-label {
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.tp-receipt-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #E4E8EF;
  padding: 18px;
  box-shadow: 0 2px 12px rgba(28,32,48,0.07);
}

.tp-receipt-card--complete {
  border-color: #A8DBBE;
  border-top: 3px solid #1A8A4A;
}

.tpr-paid-badge {
  display: inline-block;
  background: #E8F7EE;
  color: #1A8A4A;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-radius: 20px;
  padding: 3px 10px;
}

.tp-receipt-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
}

.tp-btn-outline {
  background: transparent;
  color: var(--merchant-primary-color, #2B5F7A);
  border: 1.5px solid var(--merchant-primary-color, #2B5F7A);
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
}

.tp-btn-outline:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@keyframes tpr-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.tpr-spin { animation: tpr-spin 0.8s linear infinite; }

.tpr-flash-msg {
  background: #FFF8E1;
  border: 1px solid #F5C842;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #7A5C00;
  text-align: center;
}

.tpr-flash-enter-active, .tpr-flash-leave-active { transition: opacity 0.3s, transform 0.3s; }
.tpr-flash-enter, .tpr-flash-leave-to { opacity: 0; transform: translateY(6px); }

.tp-btn-primary {
  background: var(--merchant-primary-color, #2B5F7A);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.15s;
  -webkit-tap-highlight-color: transparent;
}
</style>



// WEBPACK FOOTER //
// src/pages/hotel/concierge/TipPaymentResult.vue